/**
 * Public-facing past-session filter.
 *
 * Parents should only ever see sessions that are still available. Sessions
 * that have already finished stay in the database (rosters, payroll, and
 * financial history are untouched) — they are simply removed from every
 * public surface: the class catalog, class cards, the detail dialog, the
 * registration modal, and the weekly calendar.
 *
 * Cutoff rule: a session is hidden when its last meeting date falls before
 * the later of (a) today and (b) July 31, 2026 — the business's public
 * catalog reset date.
 */

import type { ClassItem, ClassSession } from "@/data/classes";
import { parseDateRange, semesterRangeForWeekdays } from "./nextClassSession";
import { parseWeekdays, type DayName } from "./sessionSchedule";
import { semesterIdFromLabel } from "./academicCalendar";

/** Public catalog reset date — nothing that ended before this shows publicly. */
export const PUBLIC_SESSION_CUTOFF = new Date(2026, 6, 31); // July 31, 2026

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** The floor date a session must still reach to remain publicly visible. */
export function publicSessionFloor(today: Date = new Date()): Date {
  const t = startOfDay(today);
  return t > PUBLIC_SESSION_CUTOFF ? t : PUBLIC_SESSION_CUTOFF;
}

/**
 * Resolve a session's final meeting date. Returns null when the session has no
 * parseable dates (interest lists / "dates coming soon"), which we always keep.
 */
export function resolveSessionEnd(
  session: ClassSession,
  scheduleText: string | null | undefined,
  refYear: number,
): Date | null {
  const range = parseDateRange(session.dates ?? "", refYear);
  if (range) return range.end;

  const semesterId = semesterIdFromLabel(`${session.label ?? ""} ${session.dates ?? ""}`);
  if (semesterId) {
    const sessionDays = parseWeekdays(session.dates ?? "");
    const days: DayName[] = sessionDays.length ? sessionDays : parseWeekdays(scheduleText ?? "");
    const win = semesterRangeForWeekdays(semesterId, days);
    if (win) return win.end;
  }
  return null;
}

/** True when this session has finished and should be hidden from parents. */
export function isSessionPast(
  session: ClassSession,
  scheduleText: string | null | undefined,
  today: Date = new Date(),
): boolean {
  const floor = publicSessionFloor(today);
  const end = resolveSessionEnd(session, scheduleText, floor.getFullYear());
  if (!end) return false; // undated / interest-only sessions stay visible
  return startOfDay(end) < floor;
}

/**
 * Resolve a session's first meeting date, when it can be parsed. Undated /
 * interest-only sessions return null and always sort last.
 */
export function resolveSessionStart(
  session: ClassSession,
  scheduleText: string | null | undefined,
  refYear: number,
): Date | null {
  const range = parseDateRange(session.dates ?? "", refYear);
  if (range) return range.start;

  const semesterId = semesterIdFromLabel(`${session.label ?? ""} ${session.dates ?? ""}`);
  if (semesterId) {
    const sessionDays = parseWeekdays(session.dates ?? "");
    const days: DayName[] = sessionDays.length ? sessionDays : parseWeekdays(scheduleText ?? "");
    const win = semesterRangeForWeekdays(semesterId, days);
    if (win) return win.start;
  }
  return null;
}

/**
 * Order sessions so the soonest-starting one comes first. Parents should always
 * see (and have preselected) the session that is running or starting next —
 * not whichever session happens to sit first in the stored array.
 * Stable: undated sessions keep their relative order at the end.
 */
export function sortSessionsBySoonest(
  sessions: ClassSession[],
  scheduleText: string | null | undefined,
  today: Date = new Date(),
): ClassSession[] {
  const refYear = publicSessionFloor(today).getFullYear();
  return sessions
    .map((s, i) => ({ s, i, start: resolveSessionStart(s, scheduleText, refYear) }))
    .sort((a, b) => {
      if (a.start && b.start) {
        const diff = a.start.getTime() - b.start.getTime();
        return diff !== 0 ? diff : a.i - b.i;
      }
      if (a.start) return -1;
      if (b.start) return 1;
      return a.i - b.i;
    })
    .map((x) => x.s);
}

/** Drop finished sessions from a class and order the rest soonest-first. */
export function withPublicSessions(c: ClassItem, today: Date = new Date()): ClassItem {
  if (!c.sessions || c.sessions.length === 0) return c;
  const kept = c.sessions.filter((s) => !isSessionPast(s, c.schedule, today));
  return { ...c, sessions: sortSessionsBySoonest(kept, c.schedule, today) };
}


/**
 * True when a class had scheduled sessions but every one of them has ended —
 * the class itself is over and should not appear publicly.
 */
export function isClassFullyPast(c: ClassItem, today: Date = new Date()): boolean {
  if (!c.sessions || c.sessions.length === 0) return false;
  return c.sessions.every((s) => isSessionPast(s, c.schedule, today));
}

/** Filter + clean a list of classes for any public surface. */
export function toPublicClasses(list: ClassItem[], today: Date = new Date()): ClassItem[] {
  return list.filter((c) => !isClassFullyPast(c, today)).map((c) => withPublicSessions(c, today));
}
